import api from './api';
import type { PagedResult } from '@/types/catalog';
import type {
  LearnerPathway,
  LearnerPathwayQuery,
  HistoryItem,
  HistoryQuery,
  HistorySummary,
  ActivityFeedItem,
  ActivityQuery,
  UserActivityItem,
} from '@/types/learner';
import { buildQueryString } from '@/utils/buildQueryString';

export const learnerApi = {
  // =============================================
  // Learner Pathways
  // =============================================

  /**
   * Get pathways assigned to or enrolled by the current user.
   */
  getMyPathways: async (query: LearnerPathwayQuery = {}): Promise<PagedResult<LearnerPathway>> => {
    const qs = buildQueryString(query as Record<string, unknown>);
    const response = await api.get<PagedResult<LearnerPathway>>(`/me/pathways${qs}`);
    return response.data;
  },

  /**
   * Get a single pathway with progress details.
   */
  getMyPathwayById: async (id: number): Promise<LearnerPathway> => {
    const response = await api.get<LearnerPathway>(`/me/pathways/${id}`);
    return response.data;
  },

  // =============================================
  // Learning History
  // =============================================

  /**
   * Get learning history (completed courses) for the current user.
   */
  getHistory: async (query: HistoryQuery = {}): Promise<PagedResult<HistoryItem>> => {
    const qs = buildQueryString(query as Record<string, unknown>);
    const response = await api.get<PagedResult<HistoryItem>>(`/me/history${qs}`);
    return response.data;
  },

  /**
   * At-a-glance counts and top-5 practice areas powering the History tab bar chart.
   */
  getHistorySummary: async (): Promise<HistorySummary> => {
    const response = await api.get<HistorySummary>('/me/history-summary');
    return response.data;
  },

  /**
   * Org-admin variant: same shape, scoped to a user in the caller's organization.
   */
  getOrgUserHistorySummary: async (orgId: number, userId: number): Promise<HistorySummary> => {
    const response = await api.get<HistorySummary>(
      `/organizations/${orgId}/users/${userId}/history-summary`,
    );
    return response.data;
  },

  // =============================================
  // Activity Feed
  // =============================================

  /**
   * Get activity feed for the current user.
   */
  getActivity: async (query: ActivityQuery = {}): Promise<PagedResult<ActivityFeedItem>> => {
    const qs = buildQueryString(query as Record<string, unknown>);
    const response = await api.get<PagedResult<ActivityFeedItem>>(`/me/activity${qs}`);
    return response.data;
  },

  // =============================================
  // Org Admin - User Activity
  // =============================================

  /**
   * Get activity feed for a specific user within an organization (org admin).
   */
  getOrgUserActivity: async (
    orgId: number,
    userId: number,
    query: ActivityQuery = {},
  ): Promise<PagedResult<UserActivityItem>> => {
    const qs = buildQueryString(query as Record<string, unknown>);
    const response = await api.get<PagedResult<UserActivityItem>>(
      `/organizations/${orgId}/users/${userId}/activity${qs}`,
    );
    return response.data;
  },
};

export default learnerApi;
